/**
 * /devhub/invites — Invite a user into the N-Tier tenant tree (operator). Surface Layer (/01).
 *
 * REQ-001.91 DevHub UI rebuild · Phase 2 (already-live domains). The forward (post-cutover) invite
 * surface over the live relying-party endpoints (`/admin/tenants` + `/admin/invite` via the auth
 * provider). The relying party enforces PBAC server-side (owner/admin role, caller-subtree ancestry,
 * depth = parent.depth + 1); this is the operator UX. Operator-only (routePolicy plane guard + self-guard).
 */

import {
  Button,
  Column,
  Dropdown,
  Form,
  Grid,
  InlineNotification,
  SkeletonText,
  Tag,
  TextInput,
  Tile,
  Toggle,
} from '@carbon/react';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState, type FormEvent } from 'react';

import { adminInvite, adminTenants, type TenantNode } from '@auth-provider';
import { getAccessToken } from '@auth/session-token';
import { useEntitlements } from '@lib/persistence/useEntitlements';

export const Route = createFileRoute('/devhub/invites')({ component: DevHubInvitesPage });

const INVITE_ROLES = ['owner', 'admin', 'developer', 'user', 'viewer'] as const;
type InviteRole = (typeof INVITE_ROLES)[number];

function tenantLabel(node: TenantNode): string {
  return `${'— '.repeat(Math.min(node.depth, 6))}${node.slug} · depth ${node.depth}`;
}

const INVITE_FAILURE_REASONS: Record<string, string> = {
  role_forbidden: 'Your role cannot invite users (owner/admin required).',
  outside_caller_subtree: 'Target workspace is outside your subtree.',
  owner_invite_requires_root: 'Only the depth-0 root can mint owners.',
  invalid_slug: 'Child workspace slug must be lowercase letters, numbers and hyphens (3–63 chars).',
  tenant_not_active: 'Target workspace is suspended or archived.',
  invite_failed: 'The invite e-mail could not be sent.',
  step_up_required: 'Confirm your session via the e-mail link first.',
};

function DevHubInvitesPage() {
  const { isElevated } = useEntitlements();
  const [tenants, setTenants] = useState<TenantNode[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<InviteRole>('user');
  const [target, setTarget] = useState<TenantNode | null>(null);
  const [createChild, setCreateChild] = useState(false);
  const [childSlug, setChildSlug] = useState('');
  const [childParent, setChildParent] = useState<TenantNode | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ kind: 'success' | 'error'; title: string; detail: string } | null>(
    null,
  );

  useEffect(() => {
    let active = true;
    const token = getAccessToken();
    if (!token) {
      setLoadError('Session required.');
      return;
    }
    void adminTenants(token).then((rows) => {
      if (!active) return;
      if (!rows) {
        setLoadError('Tenant tree unavailable — owner/admin role required.');
        return;
      }
      setTenants(rows);
      setTarget(rows[0] ?? null);
      setChildParent(rows[0] ?? null);
    });
    return () => {
      active = false;
    };
  }, []);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    setResult(null);
    const token = getAccessToken();
    if (!token || !email.trim()) return;
    setBusy(true);
    try {
      const payload = createChild
        ? {
            email: email.trim(),
            role,
            newTenant: { slug: childSlug.trim(), parentTenantId: childParent?.id ?? '' },
          }
        : { email: email.trim(), role, tenantId: target?.id };
      const response = await adminInvite(payload, token);
      if (response.ok) {
        setResult({
          kind: 'success',
          title: response.status === 'invited' ? 'Invite sent' : 'Membership updated',
          detail: `${email.trim()} → ${response.tenant?.slug ?? target?.slug ?? ''} (depth ${response.tenant?.depth ?? target?.depth ?? 0}) as ${role}.`,
        });
        setEmail('');
        setChildSlug('');
        if (createChild) {
          const refreshed = await adminTenants(token);
          if (refreshed) setTenants(refreshed);
        }
      } else {
        setResult({
          kind: 'error',
          title: 'Invite failed',
          detail: INVITE_FAILURE_REASONS[response.status] ?? `Unexpected status: ${response.status}`,
        });
      }
    } finally {
      setBusy(false);
    }
  }

  if (!isElevated) {
    return (
      <main className="view">
        <InlineNotification
          kind="warning"
          lowContrast
          hideCloseButton
          title="Operator access required"
          subtitle="Inviting users into the tenant tree is operator-only."
        />
      </main>
    );
  }

  return (
    <main className="view">
      <header className="view__header">
        <h1 className="view__title">Invites</h1>
        <p className="view__subtitle">Register a user into the N-Tier tenant tree. PBAC is enforced server-side.</p>
      </header>

      {loadError ? (
        <InlineNotification kind="warning" lowContrast hideCloseButton title="Invites" subtitle={loadError} />
      ) : !tenants ? (
        <SkeletonText paragraph lineCount={4} />
      ) : (
        <div className="surface-grid">
          <Grid condensed>
            <Column sm={4} md={8} lg={16}>
              <div className="view-tile__row">
                <h2 className="view-tile__value">Invite a user</h2>
                <Tag type="purple">{`${tenants.length} workspace${tenants.length === 1 ? '' : 's'} in your subtree`}</Tag>
              </div>
            </Column>
            <Column sm={4} md={8} lg={16}>
              <Tile className="view-tile">
                <Form aria-label="Invite a user" onSubmit={submit}>
                  <div className="view-stack">
                    <TextInput
                      id="invite-email"
                      type="email"
                      labelText="E-mail"
                      required
                      value={email}
                      onChange={(event) => setEmail(event.currentTarget.value)}
                    />
                    <Dropdown
                      id="invite-role"
                      titleText="Role"
                      label="Select a role"
                      items={[...INVITE_ROLES]}
                      selectedItem={role}
                      itemToString={(item: string | null) => item ?? ''}
                      onChange={({ selectedItem }: { selectedItem: string | null }) =>
                        selectedItem ? setRole(selectedItem as InviteRole) : undefined
                      }
                    />
                    <Toggle
                      id="invite-create-child"
                      labelText="Workspace target"
                      labelA="Existing workspace"
                      labelB="New child workspace"
                      toggled={createChild}
                      onToggle={(checked: boolean) => setCreateChild(checked)}
                    />
                    {createChild ? (
                      <>
                        <Dropdown
                          id="invite-child-parent"
                          titleText="Parent workspace (the new node hangs under it)"
                          label="Select the parent"
                          items={tenants}
                          selectedItem={childParent}
                          itemToString={(item: TenantNode | null) => (item ? tenantLabel(item) : '')}
                          onChange={({ selectedItem }: { selectedItem: TenantNode | null }) =>
                            selectedItem ? setChildParent(selectedItem) : undefined
                          }
                        />
                        <TextInput
                          id="invite-child-slug"
                          labelText={`New workspace slug${childParent ? ` (depth ${childParent.depth + 1})` : ''}`}
                          placeholder="e.g. acme-subsidiary"
                          required
                          value={childSlug}
                          onChange={(event) => setChildSlug(event.currentTarget.value)}
                        />
                      </>
                    ) : (
                      <Dropdown
                        id="invite-tenant"
                        titleText="Workspace"
                        label="Select the workspace"
                        items={tenants}
                        selectedItem={target}
                        itemToString={(item: TenantNode | null) => (item ? tenantLabel(item) : '')}
                        onChange={({ selectedItem }: { selectedItem: TenantNode | null }) =>
                          selectedItem ? setTarget(selectedItem) : undefined
                        }
                      />
                    )}
                    {result ? (
                      <InlineNotification
                        kind={result.kind}
                        lowContrast
                        hideCloseButton
                        title={result.title}
                        subtitle={result.detail}
                      />
                    ) : null}
                    <div className="view-actions">
                      <Button type="submit" disabled={busy || !email.trim() || (createChild && !childSlug.trim())}>
                        {busy ? 'Inviting…' : 'Send invite'}
                      </Button>
                    </div>
                  </div>
                </Form>
              </Tile>
            </Column>
          </Grid>
        </div>
      )}
    </main>
  );
}
